import { Controller } from "@hotwired/stimulus"
import { Modal, Tooltip } from "bootstrap"
import { setCreditCardFieldsRequired } from '../features/checkouts/services/form-utils'

/**
 * Checkout Controller
 * 
 * Handles checkout functionality including:
 * - Opening address selection modals
 * - Payment method tab switching
 * - Country/state selection
 * - Date validation
 * - Terms and conditions acceptance with submit button toggling
 * 
 * Note: Shipping cost updates are handled by the dedicated shipping-costs controller
 * Note: Address form submissions are handled by Turbo Streams (data-turbo-stream="true")
 * 
 * Actions:
 * - openAddressModal: Opens modal to select or enter addresses
 * - switchPaymentTab: Handles payment method tab changes
 * - handleCountryChange: Populates state dropdown when country changes
 * - validateShipDate: Validates requested ship date
 * - toggleSubmitButton: Enables/disables submit button based on terms acceptance
 * 
 * Targets:
 * - checkoutForm: Main checkout form container
 * - modalContainer: Container for modals
 * - modalBody: Modal body content area
 * - modalHeader: Modal header text area
 * - termsCheckbox: Terms and conditions checkbox
 * - submitButton: Place Order submit button
 */
export default class extends Controller {
  static targets = ["checkoutForm", "modalContainer", "modalBody", "modalHeader", "termsCheckbox", "submitButton"]
  static values = {
    addressModalUrl: String
  }

  connect() {
    // Initialize Bootstrap modals
    this.checkoutModal = null
    this.validationModal = null
    this.submitTooltip = null
    
    // Find or create modal elements
    this.initializeModals()
    
    // Set up event listeners for dynamically loaded content
    this.setupDynamicListeners()
    
    // Initialize submit button tooltip
    this.initializeSubmitTooltip()
  }

  disconnect() {
    // Clean up modals
    if (this.checkoutModal) {
      this.checkoutModal.dispose()
    }
    if (this.validationModal) {
      this.validationModal.dispose()
    }
    // Clean up tooltip
    if (this.submitTooltip) {
      this.submitTooltip.dispose()
    }
  }

  setupDynamicListeners() {
    // Shipping cost updates are now handled by the dedicated shipping-costs controller
    // This controller focuses on modals, addresses, and form handling
  }

  initializeModals() {
    // Checkout modal for address selection
    const checkoutModalEl = document.getElementById('checkout-modal')
    if (checkoutModalEl) {
      this.checkoutModal = new Modal(checkoutModalEl)
    }

    // Validation modal for address confirmation
    const validationModalEl = document.getElementById('enter-valid-modal')
    if (validationModalEl) {
      this.validationModal = new Modal(validationModalEl)
    }
  }

  // Payment method tab switching
  switchPaymentTab(event) {
    const tab = event.currentTarget
    const href = tab.getAttribute('href')
    if (!href) {
      console.error('switchPaymentTab: Missing href attribute on tab element')
      return
    }
    const targetId = href.substring(1)
    
    // Update radio button and credit card field requirements
    if (targetId === 'bill-account') {
      const billRadio = document.getElementById('payment_choice_bill_account')
      if (billRadio) billRadio.checked = true
      setCreditCardFieldsRequired(false)
    } else if (targetId === 'credit-card') {
      const cardRadio = document.getElementById('payment_choice_credit_card')
      if (cardRadio) cardRadio.checked = true
      setCreditCardFieldsRequired(true)
    }
  }

  // Handle country selection change
  handleCountryChange(event) {
    // Legacy state population logic removed in favor of Turbo Frames
  }

  // Validate ship date input
  validateShipDate(event) {
    // Date validation handled by native HTML5 validation
  }

  async openAddressModal(event) {
    event.preventDefault()
    const button = event.currentTarget
    const addressType = button.dataset.addressType
    
    if (!addressType) {
      console.error('Missing data-address-type attribute')
      return
    }
    
    // Build URL from controller's constant path
    const url = `/checkout/address_modal.json?type=${addressType}`
    
    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      // Update modal content
      this.updateModalContent(data)
      
      // Show modal
      if (this.checkoutModal) {
        this.checkoutModal.show()
      }
    } catch (error) {
      console.error('Error loading address modal:', error)
      alert('Failed to load address options. Please try again.')
    }
  }

  updateModalContent(data) {
    const modalBody = document.getElementById('checkout-modal-body')
    const modalHeader = document.getElementById('checkout-modal-header')
    
    if (modalHeader && data.header) {
      modalHeader.textContent = data.header
    }
    
    if (modalBody && data.html) {
      modalBody.innerHTML = data.html
    }
  }

  // NOTE: submitAddress and selectAddress methods removed - now handled by Turbo Streams
  // Forms use data-turbo-stream="true" and controller responds with .turbo_stream.erb templates

  getCSRFToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]')
    return tokenElement ? tokenElement.content : ''
  }

  // Initialize tooltip for submit button
  initializeSubmitTooltip() {
    if (this.hasSubmitButtonTarget) {
      this.submitTooltip = new Tooltip(this.submitButtonTarget, {
        trigger: 'hover'
      })
    }
  }

  // Toggle submit button based on terms checkbox
  toggleSubmitButton(event) {
    const checkbox = event.currentTarget
    const isChecked = checkbox.checked

    if (this.hasSubmitButtonTarget) {
      const submitButton = this.submitButtonTarget

      if (isChecked) {
        // Enable button
        submitButton.disabled = false
        submitButton.classList.remove('disabled')
        submitButton.removeAttribute('title')
        submitButton.removeAttribute('data-bs-toggle')
        
        // Dispose tooltip
        if (this.submitTooltip) {
          this.submitTooltip.dispose()
          this.submitTooltip = null
        }
      } else {
        // Disable button
        submitButton.disabled = true
        submitButton.classList.add('disabled')
        submitButton.setAttribute('title', 'Please Review the Terms and Conditions Before Proceeding')
        submitButton.setAttribute('data-bs-toggle', 'tooltip')
        
        // Reinitialize tooltip
        this.submitTooltip = new Tooltip(submitButton, {
          trigger: 'hover'
        })
      }
    }
  }
}
